import EventEmitter from 'eventemitter3';
import { isFunction } from 'lodash';
import { createMemoryHistory, MemoryHistory } from 'history';
import {
  resourceConfig,
  accountConfig,
  akfConfig,
  contentConfig,
  preparationConfig,
} from './nav-config';

function levenshteinDistance(str1: string, str2: string) {
  const matrix = [];

  matrix[0] = new Array(str1.length + 1).fill('').map((_, index) => index);

  for (let i = 1; i < str2.length + 1; i++) {
    matrix[i] = [i];
  }

  for (let m = 1; m <= str2.length; m++) {
    for (let k = 1; k <= str1.length; k++) {
      if (str2[m - 1] === str1[k - 1]) {
        matrix[m][k] = matrix[m - 1][k - 1];
      } else {
        matrix[m][k] = Math.min(matrix[m - 1][k], matrix[m][k - 1], matrix[m - 1][k - 1]) + 1;
      }
    }
  }

  return matrix[str2.length][str1.length];
}

export interface Alo7NavConf {
  link: string;
  icon?: string;
  tip: string;
  sub?: Alo7NavConf[];
  path?: Alo7NavConf[];
  params?: any;
  uuid?: string;
}

interface ParamsConfigItem {
  key: string;
  required: 'true' | 'false' | 'optinonal';
  default?: any;
  type: 'url' | 'query';
}

export class Alo7Nav {
  public eventemitter = new EventEmitter();
  public env: 'inner' | 'outer';
  private _history = createMemoryHistory();
  protected _subject: string = localStorage.getItem('subject') || '';

  // 科目
  public get subject() {
    return this._subject;
  }
  public set subject(val: string) {
    this._subject = val;
  }
  public setSubject = (subject: string) => {
    this._subject = subject;
  };

  public _config = {
    sub: [...contentConfig, ...preparationConfig, resourceConfig, akfConfig, accountConfig],
  };

  get paths() {
    const { pathname } = this.location;
    const paths = pathname.split('/');
    return paths.slice(1);
  }

  get location() {
    if (this.env === 'inner') {
      return window.location;
    } else {
      return (this.history as MemoryHistory).location;
    }
  }

  get history() {
    if (this.env === 'inner') {
      return window.history;
    } else {
      return this._history;
    }
  }

  private autoCompletePath(config: Alo7NavConf, path: string) {
    if (path === '' || config.sub.length === 1) {
      return config.sub[0];
    } else {
      return config.sub.reduce((result, next) => {
        if (result === null) {
          return next;
        } else {
          if (levenshteinDistance(result.link, path) > levenshteinDistance(next.link, path)) {
            return next;
          } else {
            return result;
          }
        }
      }, null);
    }
  }

  /**
   *
   * @param key 要查找的配置 key 值
   * @param paths 路径
   * @param autoComplete 是否自动补全，如果 paths 中有某个路径和配置不符合，是否自动找到一个最匹配的，匹配
   *                     使用编辑距离比较。如果此参数为 false, 找不到配置时，报错。
   * @param defaultValue
   */
  private getMatchedKeyByPaths<T>(
    key: string,
    paths: string[],
    autoComplete: boolean,
    defaultValue?: any
  ): T {
    let config: any = this._config;
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < paths.length; i++) {
      const item = paths[i];

      const configItem = config.sub.find((v: any) => v.link === item);
      if (configItem) {
        config = configItem;
      } else {
        if (autoComplete) {
          config = this.autoCompletePath(config, item);
        }
      }
    }

    return config[key] || defaultValue;
  }

  public searchStringToObject(search: string): any {
    const query = decodeURIComponent(search).replace(/^\?/, '').split('&');
    const queryObject = {};
    for (let i = 0, len = query.length; i < len; i++) {
      const item = query[i];
      const [key, value] = item.split('=');
      queryObject[key] = value;
    }
    return queryObject;
  }

  public objectToSearchString(params: object = {}) {
    return Object.keys(params)
      .filter((v) => params[v] !== undefined)
      .reduce((result, next, index) => {
        if (index === 0) {
          result += '?';
        }
        result += `${next}=${params[next]}&`;
        return result;
      }, '')
      .replace(/&$/, '');
  }

  /**
   * 获取跳转路径，会自动补全路径，做参数检测
   * @param pathname
   * @param params
   */
  private getUrl(pathname: string, params: object = {}) {
    const currentPaths = pathname.split('/').filter((v) => !!v);
    const queryObject = params;

    if (currentPaths.length === 0) {
      currentPaths[0] = this._config.sub[0].link;
    }

    let index = 0;
    let url = '/'; // + this.getMatchedKeyByPaths<string>('link', currentPaths.slice(0, index), true) + '/';
    let sub = this.getMatchedKeyByPaths<Alo7NavConf[]>(
      'sub',
      currentPaths.slice(0, index),
      true,
      null
    );
    while (sub) {
      if (!currentPaths[index]) {
        currentPaths[index] = '';
      }

      const item = currentPaths[index];

      if (
        this.getMatchedKeyByPaths<Alo7NavConf[]>(
          'sub',
          currentPaths.slice(0, index),
          false,
          []
        ).findIndex((v: Alo7NavConf) => v.link === item) >= 0
      ) {
        url = url + item + '/';
      } else {
        // 如果上级路径的 sub 里没有 link === item 的成员
        const link = this.getMatchedKeyByPaths<string>(
          'link',
          currentPaths.slice(0, index + 1),
          true
        );

        url = url + link + '/';
        currentPaths[index] = link;
      }

      index++;
      sub = this.getMatchedKeyByPaths<Alo7NavConf[]>(
        'sub',
        currentPaths.slice(0, index),
        false,
        null
      );
    }

    url = url.replace(/\/$/, '');

    const paramsConfig: ParamsConfigItem[] = this.getMatchedKeyByPaths(
      'params',
      url.split('/').slice(1),
      false,
      []
    );

    for (let i = 0, len = paramsConfig.length; i < len; i++) {
      const item = paramsConfig[i];
      if (item.key === 'subject') continue;
      const required = isFunction(item.required)
        ? item.required(currentPaths, queryObject)
        : item.required;
      if (required === 'true') {
        if (!queryObject[item.key]) {
          if (item.default !== void 0 || item.default !== null) {
            queryObject[item.key] = item.default;
          } else {
            throw new Error(`required param ${item.key} is not provide`);
          }
        }
      } else if (required === 'false') {
        delete queryObject[item.key];
      }
    }

    if (!!params && !!params['subject']) {
      this.subject = params['subject'];
    }
    return url + this.objectToSearchString({ ...queryObject, subject: this.subject });
  }

  public getCurrentSearchObject(): any {
    return this.searchStringToObject(this.location.search);
  }

  public getMatchedSubConfig(level = 999): Alo7NavConf[] {
    const paths = this.paths.slice(0, level - 1);
    if (paths.length < level - 1) {
      for (let i = paths.length + 1; i < level; i++) {
        const config = this.getMatchedKeyByPaths('sub', paths, true)[0];
        paths.push(config.link);
      }
    }
    return this.getMatchedKeyByPaths('sub', paths, true);
  }

  /**
   * 返回当前 url 匹配的 tip 提示字符。
   * @param level 最多搜寻到哪一级菜单
   */
  public getMatchedTip(level = 999): string {
    const paths = this.paths.slice(0, level);
    if (!paths.length) {
      return '';
    }
    let config: any = this._config.sub;
    for (let i = 0; i < paths.length; i++) {
      const item = paths[i];
      const configItem = config.find((v: any) => v.link === item);
      if (configItem) {
        config = i === paths.length - 1 ? configItem : configItem.sub;
      }
    }
    return config.tip;
  }

  public getMatchedKey(level = 999) {
    return this.paths[level - 1];
  }

  // todo goto 没有处理无效链接, 没有处理默认参数
  /**
   *
   * @param paths 跳转路径
   */
  public goto(paths: [string, string, string] | string, params?: object) {
    const prefix = typeof paths !== 'string' ? '/' + paths.join('/') : paths;
    const _url = typeof paths !== 'string' ? this.getUrl(prefix, params) : prefix;
    const url = Alo7Nav.toURL(_url);
    if (this.env === 'inner') {
      window.history.pushState(null, null, url);
    } else {
      (this.history as MemoryHistory).push(url);
    }
    setTimeout(() => this.eventemitter.emit('popstate', { path: url }), 0);
  }

  public gotoNewTab(paths: [string, string, string], params?: object) {
    const _url = this.getUrl('/' + paths.join('/'), params);
    const url = Alo7Nav.toURL(_url);
    window.open(url);
  }

  public redirectTo(paths: string[], params?: object) {
    const _url = this.getUrl('/' + paths.join('/'), params);
    const url = Alo7Nav.toURL(_url);
    if (this.env === 'inner') {
      window.history.replaceState(null, null, url);
    } else {
      (this.history as MemoryHistory).replace(url);
    }
    this.eventemitter.emit('popstate', {
      path: url,
    });
  }

  public onWindowPopstate(paths: string[], level: number, params?: object) {
    const prePath = this.paths.slice(0, level - 1);
    const url = this.getUrl('/' + prePath.concat(paths).join('/'), params);
    this.eventemitter.emit('popstate', {
      path: url,
    });
  }

  public gotoDefault(basePath: string) {
    if (basePath === '/home/none') return;
    if (!basePath) {
      basePath = `/${this._config.sub[0].link}`;
    }

    const queryObject = this.searchStringToObject(this.location.search);
    const url = this.getUrl(basePath, queryObject);
    if (this.env === 'inner') {
      window.history.replaceState(null, null, url);
    } else {
      (this.history as MemoryHistory).replace(url);
    }
  }

  /**
   * @param link
   * @param truncate 指示要截断几个路径值，从左开始截断
   */
  public toRouterStyle(link: string, truncate = 0) {
    const decodedLink = decodeURIComponent(link);
    const [path, search] = decodedLink.split('?');
    const searchObject = this.searchStringToObject(search);
    const paths = path.split('/').slice(1);
    let result = '/' + paths.slice(truncate).join('/');
    const searchObj = {};
    const paramsConfig: ParamsConfigItem[] = this.getMatchedKeyByPaths('params', paths, false, []);
    for (let i = 0, len = paramsConfig.length; i < len; i++) {
      const item = paramsConfig[i];
      const required = isFunction(item.required)
        ? item.required(paths, searchObject)
        : item.required;
      if (item.type === 'query') {
        if (required === 'true') {
          if (searchObject[item.key]) {
            searchObj[item.key] = searchObject[item.key];
          } else {
            throw new Error(`required param ${item.key} is not provide`);
          }
        } else if (required === 'optional') {
          if (searchObject[item.key]) {
            searchObj[item.key] = searchObject[item.key];
          }
        }
      } else {
        if (required === 'true') {
          if (searchObject[item.key]) {
            result += `/${searchObject[item.key]}`;
          } else {
            throw new Error(`required param ${item.key} is not provide`);
          }
        } else if (required === 'optional') {
          if (searchObject[item.key]) {
            result += `/${searchObject[item.key]}`;
          }
        }
      }
    }
    return result + this.objectToSearchString(searchObj);
  }

  private onPopstate() {
    this.onWindowPopstate(this.paths, 1, this.searchStringToObject(this.location.search));
  }

  public setEnv(env: 'inner' | 'outer') {
    if (env !== this.env) {
      if (this.env === 'inner') {
        window.removeEventListener('popstate', this.onPopstate.bind(this));
      } else {
        window.addEventListener('popstate', this.onPopstate.bind(this));
      }
    }
    this.env = env;
  }

  /**
   * 所有跳转方法的URL最终都会到这里来
   * @private
   */
  public static toURL(inUrl) {
    const { schoolId, platform } = alo7Account;
    return nx.UrlOperator.update({ schoolId, platform, subject: 'english' }, inUrl);
  }
}

export const alo7Nav = new Alo7Nav();

window['alo7Nav'] = alo7Nav;
alo7Nav.setEnv('inner');
